import type { BizyState } from '../../bizy';
import type { ChatMessage } from '../hooks/use-bizy-global-chat';

// v2 (INS-725 Phase 1.4): assistantMessage markdown nodes use `{type, payload: {content}}`.
// Bumped to drop sessions persisted with the legacy `{type, content}` shape so
// the document renderer doesn't crash reading `node.payload.content` on a
// node that survived sessionStorage from a pre-refactor session.
const STORAGE_KEY = 'bizy-admin-chat-session-v2';
// Older versions of this storage key — cleared on every save/clear so they
// don't linger indefinitely in tabs that were open across the bump.
const LEGACY_STORAGE_KEYS = ['bizy-admin-chat-session'];
const MAX_AGE_MS = 4 * 60 * 60 * 1000; // 4 hours

export interface BizySessionData {
	messages: ChatMessage[];
	conversationId?: string;
	bizyEmotion: BizyState;
	lastUpdated: number;
}

/**
 * Save Bizy chat session to sessionStorage
 * Automatically adds timestamp for staleness checking
 */
export function saveBizySession(data: Omit<BizySessionData, 'lastUpdated'>): void {
	try {
		const sessionData: BizySessionData = {
			...data,
			lastUpdated: Date.now(),
		};
		sessionStorage.setItem(STORAGE_KEY, JSON.stringify(sessionData));
		// Best-effort eviction of any pre-v2 entry left over from before the bump.
		LEGACY_STORAGE_KEYS.forEach((key) => sessionStorage.removeItem(key));
	} catch (error) {
		// Gracefully handle sessionStorage errors (e.g., quota exceeded, disabled in incognito)
		console.warn('Failed to save Bizy session to sessionStorage:', error);
	}
}

/**
 * Load Bizy chat session from sessionStorage
 * Returns null if data doesn't exist, is corrupted, or is stale (> 4 hours old)
 */
export function loadBizySession(): BizySessionData | null {
	try {
		const stored = sessionStorage.getItem(STORAGE_KEY);
		if (!stored) return null;

		const data = JSON.parse(stored) as BizySessionData;

		// Check if data is stale (> 4 hours old)
		const age = Date.now() - data.lastUpdated;
		if (age > MAX_AGE_MS) {
			clearBizySession();
			return null;
		}

		// Revive timestamp strings back to Date objects (JSON.stringify serializes Dates as ISO strings)
		data.messages = data.messages.map((msg) => ({
			...msg,
			timestamp: msg.timestamp ? new Date(msg.timestamp) : undefined,
		}));

		return data;
	} catch (error) {
		// Handle JSON parse errors or sessionStorage access errors
		console.warn('Failed to load Bizy session from sessionStorage:', error);
		clearBizySession(); // Clear corrupted data
		return null;
	}
}

/**
 * Clear Bizy chat session from sessionStorage. Also evicts legacy keys from
 * before the v2 bump so they don't sit in storage indefinitely.
 */
export function clearBizySession(): void {
	try {
		sessionStorage.removeItem(STORAGE_KEY);
		LEGACY_STORAGE_KEYS.forEach((key) => sessionStorage.removeItem(key));
	} catch (error) {
		console.warn('Failed to clear Bizy session from sessionStorage:', error);
	}
}
